/**
 * Custom edge component for Mermaid diagrams
 * Supports different line styles and arrow types
 */

import { useCallback, useState } from "react"

import { BaseEdge, EdgeLabelRenderer, getSmoothStepPath, useReactFlow } from "@xyflow/react"

import type { MermaidEdgeData } from "../../utils"
import type { Position } from "@xyflow/react"

import { Pencil, Trash2, X } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"

type MermaidEdgeProps = {
  id: string
  sourceX: number
  sourceY: number
  targetX: number
  targetY: number
  sourcePosition: Position
  targetPosition: Position
  data?: MermaidEdgeData
  selected?: boolean
  label?: string
}

function getEdgeStyle(style: MermaidEdgeData["style"]): React.CSSProperties {
  switch (style) {
    case "dotted":
      return { strokeDasharray: "5,5" }
    case "thick":
      return { strokeWidth: 3 }
    case "invisible":
      return { opacity: 0.2, strokeDasharray: "2,2" }
    case "solid":
    default:
      return {}
  }
}

function getMarkerEnd(targetArrow: MermaidEdgeData["targetArrow"]): string | undefined {
  switch (targetArrow) {
    case "arrow":
      return "url(#arrow)"
    case "circle":
      return "url(#circle)"
    case "cross":
      return "url(#cross)"
    default:
      return undefined
  }
}

function getMarkerStart(sourceArrow: MermaidEdgeData["sourceArrow"]): string | undefined {
  switch (sourceArrow) {
    case "arrow":
      return "url(#arrow-reverse)"
    case "circle":
      return "url(#circle)"
    case "cross":
      return "url(#cross)"
    default:
      return undefined
  }
}

export function MermaidEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  selected,
  label,
}: MermaidEdgeProps) {
  const { setEdges } = useReactFlow()
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(String(label || data?.label || ""))

  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  })

  const edgeStyle = data ? getEdgeStyle(data.style) : {}
  const markerEnd = data ? getMarkerEnd(data.targetArrow) : "url(#arrow)"
  const markerStart = data ? getMarkerStart(data.sourceArrow) : undefined

  const handleDelete = useCallback(() => {
    setEdges((edges) => edges.filter((edge) => edge.id !== id))
  }, [id, setEdges])

  const handleSave = useCallback(() => {
    setEdges((edges) =>
      edges.map((edge) =>
        edge.id === id
          ? {
              ...edge,
              label: editValue || undefined,
              data: { ...edge.data, label: editValue || undefined },
            }
          : edge
      )
    )
    setIsEditing(false)
  }, [id, editValue, setEdges])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Enter") {
        handleSave()
      } else if (event.key === "Escape") {
        setIsEditing(false)
        setEditValue(String(label || data?.label || ""))
      }
    },
    [handleSave, label, data?.label]
  )

  return (
    <>
      <BaseEdge
        id={id}
        markerEnd={markerEnd}
        markerStart={markerStart}
        path={edgePath}
        style={{
          ...edgeStyle,
          stroke: selected ? "hsl(var(--primary))" : "hsl(var(--muted-foreground))",
          strokeWidth: edgeStyle.strokeWidth || 2,
        }}
      />
      <EdgeLabelRenderer>
        <div
          className="nodrag nopan pointer-events-auto absolute"
          style={{
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
          }}
        >
          {isEditing ? (
            <div className="bg-background flex items-center gap-1 rounded-md p-1 shadow-md">
              <Input
                className="h-6 w-24 border-none p-1 text-xs focus-visible:ring-0"
                placeholder="Label"
                value={editValue}
                onBlur={handleSave}
                onChange={(event) => setEditValue(event.target.value)}
                onKeyDown={handleKeyDown}
              />
              <Button
                className="-m-0.5 size-6 p-0"
                size="icon"
                variant="ghost"
                onClick={() => {
                  setIsEditing(false)
                  setEditValue(String(label || data?.label || ""))
                }}
              >
                <X className="size-3" />
              </Button>
            </div>
          ) : (
            <div
              className={cn(
                "group flex items-center gap-1 rounded-md px-2 py-0.5 text-xs",
                label || data?.label ? "bg-background shadow-sm" : "",
                selected && "ring-primary ring-1"
              )}
            >
              {(label || data?.label) && (
                <span className="text-muted-foreground">{label || data?.label}</span>
              )}
              {selected && (
                <div className="flex items-center gap-0.5">
                  <Button
                    className="-m-0.5 size-6 p-0 opacity-0 transition-opacity group-hover:opacity-100"
                    size="icon"
                    variant="ghost"
                    onClick={() => {
                      setEditValue(String(label || data?.label || ""))
                      setIsEditing(true)
                    }}
                  >
                    <Pencil className="size-3" />
                  </Button>
                  <Button
                    className="text-destructive -m-0.5 size-6 p-0 opacity-0 transition-opacity group-hover:opacity-100"
                    size="icon"
                    variant="ghost"
                    onClick={handleDelete}
                  >
                    <Trash2 className="size-3" />
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </EdgeLabelRenderer>
    </>
  )
}
